<template>
    <div
        v-if="!!contact"
        class="nibnut-aspect-ratio-box nibnut-ratio-square contact-avatar"
    >
        <div>
            <form-image-input
                :id="id"
                :name="name"
                :value="contact.remote_image_url || contact.image_url"
                :required="required"
                :editable="editable"
                @click="edit"
                @upload="upload_file_list('image_url', $event)"
                @remove="confirm_clear_file('image_url')"
            >
                <template v-slot:read_only>
                    <div class="form-image-input-empty uk-flex uk-flex-column uk-flex-center uk-flex-middle uk-text-center">
                        <img v-if="contact.remote_image_url || contact.image_url" :src="contact.remote_image_url || contact.image_url" />
                        <div v-else-if="initials" class="uk-text-center uk-text-large uk-text-uppercase">
                            {{ initials }}
                        </div>
                    </div>
                </template>
            </form-image-input>

            <modal-dialog
                :id="`contact-avatar-dialog-${id}`"
                :show.sync="editing"
            >
                <template v-slot:title>
                    Setup Avatar for {{ contact.name }}
                </template>

                <form-input
                    ref="remote_image_url"
                    :id="`contact-avatar-remote_image_url-${id}`"
                    name="remote_image_url"
                    v-model="remote_image_url"
                    placeholder="Image URL"
                    :editable="true"
                    :required="false"
                    class="uk-width-1-1"
                />

                <div class="uk-divider uk-text-center" data-content="OR"></div>

                <div class="uk-form-file uk-width-1-1 uk-text-center uk-margin-bottom">
                    Click here to select a file on your computer...
                    <input
                        v-if="file_field_ready"
                        type="file"
                        :accept="upload_accepts()"
                        @change="set_upload_file"
                    >
                </div>

                <div class="uk-modal-footer uk-text-center">
                    <div class="uk-grid">
                        <div class="uk-width-1-2">
                            <base-button
                                size="small"
                                width="1-1"
                                :disabled="saving_editor"
                                @click.prevent="edit = false"
                                class="uk-width-1-1"
                            >
                                Cancel
                            </base-button>
                        </div>
                        <div class="uk-width-1-2">
                            <base-button
                                :waiting="saving_editor"
                                :disabled="saving_editor"
                                color="primary"
                                size="small"
                                width="1-1"
                                @click.prevent="save"
                                class="uk-width-1-1"
                            >
                                <span v-if="!remote_image_url && !!local_file">Upload {{ local_file.name }}</span>
                                <span v-else>Save</span>
                            </base-button>
                        </div>
                    </div>
                </div>
            </modal-dialog>
        </div>
    </div>
</template>

<script>
import handles_saving from "../mixins/HandlesSaving"
import handles_uploads from "../mixins/HandlesUploads"

import ModalDialog from "../dialogs/ModalDialog/ModalDialog"
import FormImageInput from "./FormImageInput"
import FormInput from "./FormInput"
import BaseButton from "./BaseButton"

export default {
    name: "ContactAvatar",
    mixins: [handles_saving, handles_uploads],
    components: {
        ModalDialog,
        FormImageInput,
        FormInput,
        BaseButton
    },
    mounted () {
        this.reset_record_id()
    },
    watch: {
        "contact.id": "reset_record_id"
    },
    methods: {
        reset_record_id () {
            this.record_id = this.contact ? this.contact.id : 0
        },
        upload_accepts (name, filename = null) {
            if(filename) return !!filename.match(/\.(png|jpe?g)$/i)
            return "image/jpeg,image/png,.jpg,.jpeg,.png"
        },
        set_upload_file (event) {
            if(!this.editable) return
            this.remote_image_url = ""
            this.local_file = event.target.files[0]
            this.file_field_ready = false
            this.$nextTick(() => {
                this.file_field_ready = true
            })
        },
        upload_data (name) {
            return { entity: "contact", id: this.contact.id, data: { type: "image" } }
        },
        confirm_clear_file (name) {
            this.confirming_clear_file = name
            this.$confirm({
                type: "error",
                title: this.contact.remote_image_url ? "Unlink Image" : "Remove uploaded file",
                message: this.contact.remote_image_url ? "Do you really want to unlink this image? Make sure you have a copy of the URL if you think you'll need it again some day." : "Do you really want to delete the uploaded file? Make sure you have a copy if you think you'll need it again some day.",
                cancel: "Keep It",
                ok: this.contact.remote_image_url ? "Unlink It" : "Remove It",
                then: (confirmed) => {
                    if(confirmed) this.clear_file()
                }
            })
        },
        clear_file () {
            this.clearing_file = true
            if(this.contact.remote_image_url) {
                this.save_field(null, "remote_image_url")
            } else {
                this.$store.dispatch("FILE_ACTION", {
                    ...this.upload_data(this.confirming_clear_file, "clear"),
                    name: this.confirming_clear_file,
                    action: this.detach_action
                }).then(response => {
                    this.upload_detached(this.confirming_clear_file, response)
                }).catch(error => {
                    this.upload_detach_error(this.confirming_clear_file, error)
                }).then(() => {
                    this.clearing_file = false
                })
            }
        },
        edit () {
            if(!this.editable) return
            this.remote_image_url = this.contact.remote_image_url
            this.local_file = null
            this.editing = true
            setTimeout(() => {
                const node = this.$refs.remote_image_url.$el.querySelector("input")
                if(node) {
                    node.focus()
                    node.select()
                }
            }, 150)
        },
        save () {
            const done_saving = () => {
                this.saving_editor = false
                this.editing = false
            }
            this.saving_editor = true
            if(this.remote_image_url) {
                this.save_field(this.remote_image_url, "remote_image_url").catch(error => {
                    this.$error(error.message || error)
                }).then(() => {
                    done_saving()
                })
            } else if(this.local_file) {
                this.upload_file("image_url", this.local_file).then(() => {
                    this.save_field(null, "remote_image_url").catch(error => {
                        this.$error(error.message || error)
                    }).then(() => {
                        done_saving()
                    })
                })
            } else done_saving()
        }
    },
    computed: {
        initials () {
            if(this.contact && (this.contact.first_name || this.contact.last_name)) {
                const initials = []
                if(this.contact.first_name) initials.push(this.contact.first_name.substr(0, 1))
                if(this.contact.last_name) initials.push(this.contact.last_name.substr(0, 1))
                return initials.join("")
            }
            return null
        }
    },
    props: {
        contact: {
            type: Object,
            required: true
        },
        id: {
            type: String,
            validator: prop => !!prop
        },
        name: {
            type: String,
            validator: prop => !!prop,
            default: "image_url"
        },
        required: {
            type: Boolean,
            required: true
        },
        editable: {
            type: Boolean,
            default: false
        }
    },
    data () {
        return {
            entity: "contact",
            remote_image_url: "",
            local_file: null,
            editing: false,
            file_field_ready: true,
            saving_editor: false
        }
    }
}
</script>

<style lang="less">
@import "@/assets/less/variables";

.contact-avatar {
    .uk-text-large {
        font-size: 3em;
    }
}
</style>
